import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  Dropdown,
  ToggleButton,
  ButtonGroup,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaFileExcel,
  FaCheckCircle,
  FaUpload,
  FaUserTie,
  FaMapMarkedAlt,
  FaFileAlt,
  FaGlobe,
  FaMobileAlt,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaComment,
  FaGlobeAsia,
  FaMapPin,
  FaCity,
  FaMap,
  FaHome,
  FaIdCard,
  FaFileInvoice,
  FaArrowLeft,
  FaArrowRight,
  FaSpinner,
  FaInfoCircle,
} from "react-icons/fa";
import { User, fetchUsersByClientId } from "../../../api/todo";
import { LeadSource, fetchAllLeadSourcesActive } from "../../../api/leadsource";
import { RootState } from "../../../core/data/redux/store";
import {
  importContacts,
  ImportContactsOptions,
  ImportContactsResult,
  ImportError,
} from "../../../api/contact";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import { toast } from "react-toastify";

interface CountryCode {
  code: string;
  name: string;
  flag: string;
}

const countryCodes: CountryCode[] = [
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+1", name: "United States", flag: "🇺🇸" },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
  { code: "+49", name: "Germany", flag: "🇩🇪" },
  { code: "+33", name: "France", flag: "🇫🇷" },
  { code: "+81", name: "Japan", flag: "🇯🇵" },
  { code: "+86", name: "China", flag: "🇨🇳" },
  { code: "+55", name: "Brazil", flag: "🇧🇷" },
];

const contactFields = [
  "company_name",
  "company_mail",
  "company_phone",
  "contact_remark",
  //"website_url",
  "contact_person_name",
  "contact_person_desigination",
  //"leadsource_id",
  "contact_notes",
  "branch_name_1",
  "branch_address_1",
  "branch_city_1",
  "branch_state_1",
  "branch_zipcode_1",
  "branch_country_1",
  "gst_number_1",
  "branch_name_2",
  "branch_address_2",
  "branch_city_2",
  "branch_state_2",
  "branch_zipcode_2",
  "branch_country_2",
  "gst_number_2",
];

interface FormData {
  importType: "lead-upload";
  actionType: "add" | "update";
  updateBasedOn: "mobile";
  file: File | null;
  mobileCountryCodeCheck: boolean;
  selectedCountryCode?: CountryCode;
  allowDuplicatesEmail: boolean;
  allowDuplicatesMobile: boolean;
  leadAssignmentType: "single" | "file-based" | null;
  singleLeadOwner?: User;
  fileLeadOwnerColumn?: string;
  fieldMappings: { [key: string]: string };
  leadOwnersMap: { [key: string]: User };
  leadSourcesMap: { [key: string]: LeadSource };
  leadSourceType: "single" | "file-based" | null;
  singleLeadSource?: LeadSource;
  fileLeadSourceColumn?: string;
}

const ImportPage: React.FC = () => {
  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({
    importType: "lead-upload",
    actionType: "add",
    updateBasedOn: "mobile",
    file: null,
    mobileCountryCodeCheck: true,
    allowDuplicatesEmail: false,
    allowDuplicatesMobile: false,
    leadAssignmentType: null,
    fieldMappings: {},
    leadOwnersMap: {},
    leadSourcesMap: {},
    leadSourceType: null,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const clientId = useSelector((state: RootState) => state.auth.client_id);
  const [uploadErrors, setUploadErrors] = useState<ImportError[]>([]);
  // after existing useState declarations
  const [excelRows, setExcelRows] = useState<any[]>([]); // parsed rows (objects keyed by header)
  const [valueMappings, setValueMappings] = useState<
    Record<string, Record<string, string>>
  >({});
  // shape: { "<columnName>": { "<fileValue>": "<mappedId>" } }
  // Add this new state at top with others
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const navigate = useNavigate();
  const route = all_routes;

  useEffect(() => {
    fetchUsers();
    fetchLeadSources();
  }, [clientId]);

  const fetchUsers = async () => {
    if (clientId) {
      try {
        const data = await fetchUsersByClientId(clientId);
        setUsers(data.filter((u) => !u.is_deleted && u.status));
      } catch (error) {
        console.error("Failed to fetch users");
      }
    }
  };

 const fetchLeadSources = async () => {
      const data = await fetchAllLeadSourcesActive();
      setLeadSources(data);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith(".xlsx")) {
      setFormData((prev) => ({ ...prev, file }));
      parseExcel(file);
      setValidationErrors([]);
    } else {
      setValidationErrors(["Please select a valid XLSX file."]);
    }
  };

  const parseExcel = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // headers (first row)
      const headers = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      })[0] as string[];
      setExcelHeaders(headers || []);
      const initialMappings: { [key: string]: string } = {};
      headers.forEach((header) => (initialMappings[header] = ""));
      setFormData((prev) => ({ ...prev, fieldMappings: initialMappings }));

      // full rows as objects {header1: val, header2: val, ...}
      const rows = XLSX.utils.sheet_to_json(worksheet, {
        defval: null,
      }) as any[];
      setExcelRows(rows || []);
      // reset any previous valueMappings for a clean start
      setValueMappings({});
    };
    reader.readAsBinaryString(file);
  };

  const uniqueValuesForColumn = (col: string): string[] => {
    if (!excelRows || excelRows.length === 0) return [];
    const s = new Set<string>();
    excelRows.forEach((r) => {
      const v = r[col];
      if (v !== undefined && v !== null && String(v).toString().trim() !== "") {
        s.add(String(v).trim());
      }
    });
    return Array.from(s);
  };

  const validateStep = (currentStep: number): boolean => {
    let errors: string[] = [];
    switch (currentStep) {
      case 1:
        if (!formData.file) errors.push("File is required.");
        if (formData.actionType === "update" && !formData.updateBasedOn)
          errors.push("Update based on field is required.");
        break;
      case 2:
        if (formData.actionType === "add") {
          // Optional validations
        }
         if (!formData.fileLeadSourceColumn) {
           errors.push("Lead source column is required.")
        }
        break;
      case 3: {
        const mappedCount = Object.values(formData.fieldMappings).filter(
          (v) => v && v.trim() !== ""
        ).length;

        if (mappedCount === 0) {
          errors.push("At least one field mapping is required.");
        }

        break;
      }
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
  };

  const handleInputChange = (key: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleMappingChange = (header: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      fieldMappings: { ...prev.fieldMappings, [header]: value },
    }));
    setValidationErrors([]);
  };

  const matchLeadOwners = (headerValue: string): User | undefined => {
    return users.find(
      (u) =>
        u.name.toLowerCase().includes(headerValue.toLowerCase()) ||
        headerValue.toLowerCase().includes(u.name.toLowerCase())
    );
  };

  const matchLeadSource = (headerValue: string): LeadSource | undefined => {
    return leadSources.find((ls) =>
      ls.LeadSource_name.toLowerCase().includes(headerValue.toLowerCase())
    );
  };

  const handleLeadOwnerMapping = (header: string, value: string) => {
    if (value === "lead_owner") {
      // Trigger matching logic if needed
    }
  };

  const renderStep1 = () => (
    <Card className="shadow-sm border-0 rounded-4 overflow-hidden glass-card animate__fadeInUp">
      <Card.Header className="bg-gradient-primary text-white p-4">
        <div className="d-flex align-items-center">
          <FaFileExcel className="me-3 fs-4" />
          <h5 className="mb-0 fw-bold">Step 1: Upload Your File</h5>
        </div>
      </Card.Header>
      <Card.Body className="p-5 bg-glass">
        {validationErrors.length > 0 && (
          <Alert
            variant="danger"
            className="rounded-3 p-3 shadow-sm animate__shakeX"
          >
            {validationErrors.map((err, idx) => (
              <div key={idx} className="d-flex align-items-center">
                <FaInfoCircle className="me-2 text-danger" />
                {err}
              </div>
            ))}
          </Alert>
        )}
        <Form>
          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center fw-semibold">
              <FaCheckCircle className="me-2 text-primary" />
              Import Type
            </Form.Label>
            <div className="bg-glass p-3 rounded-3 border border-light">
              <Form.Check
                type="radio"
                label="Lead Upload"
                checked
                className=" text-muted"
                aria-label="Excel contact upload"
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center fw-semibold">
              <FaUpload className="me-2" />
              Action Type *
            </Form.Label>
            <div className="d-flex gap-3">
              {/* Add New Record */}
              <div
                onClick={() => handleInputChange("actionType", "add")}
                className={`flex-fill p-3 rounded-4 border text-center shadow-sm cursor-pointer transition-all ${
                  formData.actionType === "add"
                    ? "bg-primary text-white border-primary"
                    : "bg-light text-dark border-light hover-border-primary"
                }`}
                style={{
                  minHeight: "50px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <FaFileAlt
                  className={`fs-3 mb-2 ${
                    formData.actionType === "add"
                      ? "text-white"
                      : "text-primary"
                  }`}
                />
                <strong>Add New Record</strong>
              </div>

              {/* Update Existing Record */}
              <div
                onClick={() => handleInputChange("actionType", "update")}
                className={`flex-fill p-3 rounded-4 border text-center shadow-sm cursor-pointer transition-all ${
                  formData.actionType === "update"
                    ? "bg-primary text-white border-primary"
                    : "bg-light text-dark border-light hover-border-primary"
                }`}
                style={{
                  minHeight: "50px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <FaUserTie
                  className={`fs-3 mb-2 ${
                    formData.actionType === "update"
                      ? "text-white"
                      : "text-primary"
                  }`}
                />
                <strong>Update Existing Record</strong>
              </div>
            </div>

            {formData.actionType === "update" && (
              <Form.Group className="mt-3">
                <Form.Label>Match existing records by</Form.Label>
                <Form.Select
                  value={formData.updateBasedOn}
                  onChange={(e) =>
                    handleInputChange("updateBasedOn", e.target.value as any)
                  }
                  className="rounded-3 shadow-sm custom-select"
                  aria-label="Update based on field"
                >
                  <option value="mobile">Mobile Number</option>
                  <option value="email">Email</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Choose the field used to locate existing contacts for update
                  (this will be sent as Check_Base).
                </Form.Text>
              </Form.Group>
            )}
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center fw-semibold">
              <FaFileExcel className="me-2" />
              Upload File *
            </Form.Label>
            <div className="input-group rounded-3 shadow-sm">
              <Form.Control
                type="file"
                accept=".xlsx"
                onChange={handleFileUpload}
                key={formData.file ? formData.file.name : "new"}
                aria-label="Upload XLSX file"
              />

              <span className="input-group-text bg-primary text-white rounded-start-3">
                <FaUpload />
              </span>
            </div>
            {formData.file && (
              <div className="mt-2 text-success small">
                ✅ Selected file: <strong>{formData.file.name}</strong>
              </div>
            )}
            <Form.Text className="text-muted mt-2 d-block">
              <small>
                Max records: 10K for new, 50K for updates.
                <br />
                Date Format: DD-MM-YYYY
                <br />
                DateTime Format: DD-MM-YYYY HH:MM:SS
                <br />
                Mobile Format: +91XXXXXXXXXX (with or without country code both
                are valid)
              </small>
            </Form.Text>
          </Form.Group>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderStep2 = () => (
    <Card className="shadow-sm border-0 rounded-4 overflow-hidden glass-card animate__fadeInUp">
      <Card.Header className="bg-gradient-primary text-white p-4">
        <div className="d-flex align-items-center">
          <FaMapMarkedAlt className="me-3 fs-4" />
          <h5 className="mb-0 fw-bold">Step 2: Configure Actions</h5>
        </div>
      </Card.Header>
      <Card.Body className="p-5 bg-glass">
        {validationErrors.length > 0 && (
          <Alert
            variant="danger"
            className="rounded-3 p-3 shadow-sm animate__shakeX"
          >
            {validationErrors.map((err, idx) => (
              <div key={idx} className="d-flex align-items-center">
                <FaInfoCircle className="me-2 text-danger" />
                {err}
              </div>
            ))}
          </Alert>
        )}
        <Form>
          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center fw-semibold">
              <FaGlobe className="me-2" />
              Mobile Country Code Confirmation *
            </Form.Label>
            <div className="d-flex gap-3">
              <Button
                variant={
                  formData.mobileCountryCodeCheck
                    ? "success"
                    : "outline-success"
                }
                className="flex-fill"
                onClick={() =>
                  handleInputChange("mobileCountryCodeCheck", true)
                }
              >
                <FaCheckCircle className="me-2" /> Yes (auto detect)
              </Button>
              <Button
                variant={
                  !formData.mobileCountryCodeCheck
                    ? "warning"
                    : "outline-warning"
                }
                className="flex-fill"
                onClick={() => {
                  handleInputChange("mobileCountryCodeCheck", false);
                  setShowCountryDropdown(true);
                }}
              >
                <FaMobileAlt className="me-2" /> No (choose manually)
              </Button>
            </div>

            {!formData.mobileCountryCodeCheck && (
              <div className="position-relative mt-3">
                <Button
                  variant="outline-secondary"
                  className="rounded-3 text-start w-100 shadow-sm custom-select d-flex align-items-center justify-content-between"
                  onClick={() => setShowCountryDropdown((prev) => !prev)}
                >
                  <div className="d-flex align-items-center">
                    <FaGlobe className="me-2" />
                    {formData.selectedCountryCode
                      ? `${formData.selectedCountryCode.flag} ${formData.selectedCountryCode.code} - ${formData.selectedCountryCode.name}`
                      : "Select Country"}
                  </div>
                  <FaArrowRight
                    className={`ms-2 transition-all ${
                      showCountryDropdown ? "rotate-90" : ""
                    }`}
                  />
                </Button>

                {showCountryDropdown && (
                  <div
                    className="position-absolute w-100 mt-2 rounded-3 shadow-lg bg-white border"
                    style={{
                      zIndex: 1000,
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {countryCodes.map((country) => (
                      <div
                        key={country.code}
                        onClick={() => {
                          handleInputChange("selectedCountryCode", country);
                          setShowCountryDropdown(false);
                        }}
                        className="d-flex align-items-center py-2 px-3 hover-bg"
                        style={{ cursor: "pointer" }}
                      >
                        <span className="me-2">{country.flag}</span>
                        <span>
                          {country.code} - {country.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Form.Group>
          {(formData.actionType === "add" ||
            formData.actionType === "update") && (
            <Form.Group className="mb-4">
              <Form.Label className="d-flex align-items-center fw-semibold">
                <FaUserTie className="me-2" />
                Duplicate Record Check
              </Form.Label>
              <div className="p-3 border rounded-3 bg-glass">
                <Form.Check
                  type="radio"
                  id="dup-email"
                  label="Check based on Email"
                  name="duplicateCheck"
                  checked={formData.allowDuplicatesEmail}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      allowDuplicatesEmail: true,
                      allowDuplicatesMobile: false,
                    }))
                  }
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  id="dup-mobile"
                  label="Check based on Mobile"
                  name="duplicateCheck"
                  checked={formData.allowDuplicatesMobile}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      allowDuplicatesEmail: false,
                      allowDuplicatesMobile: true,
                    }))
                  }
                />
                <Form.Text muted>
                  Only one duplicate check type can be selected at a time.
                </Form.Text>
              </div>
            </Form.Group>
          )}
            {/* Lead Source Selection */}
          <Form.Group className="mb-4 mt-5">
            <Form.Label className="d-flex align-items-center fw-semibold">
              <FaBriefcase className="me-2" />
              Lead Source Assignment *
            </Form.Label>
            <Form.Select
              value={formData.leadSourceType || ""}
              onChange={(e) =>
                handleInputChange(
                  "leadSourceType",
                  e.target.value as "single" | "file-based" | null
                )
              }
              className="rounded-3 shadow-sm custom-select mb-3"
              aria-label="Select lead source type"
            >
              <option value="">None</option>
              <option value="single">Select Single Lead Source</option>
              {excelHeaders.length > 0 && (
                <option value="file-based">Assign Based on File Column</option>
              )}
            </Form.Select>

            {/* If Single Lead Source */}
            {formData.leadSourceType === "single" && (
              <Form.Select
                value={formData.singleLeadSource?.LeadSource_id || ""}
                onChange={(e) => {
                  const source = leadSources.find(
                    (ls) => ls.LeadSource_id === e.target.value
                  );
                  handleInputChange("singleLeadSource", source);
                }}
                className="rounded-3 shadow-sm custom-select"
                aria-label="Select single lead source"
              >
                <option value="">Select Lead Source</option>
                {leadSources.map((ls) => (
                  <option key={ls.LeadSource_id} value={ls.LeadSource_id}>
                    {ls.LeadSource_name} ({ls.LeadSource_id})
                  </option>
                ))}
              </Form.Select>
            )}

            {/* If File-Based Lead Source */}
            {formData.leadSourceType === "file-based" &&
              excelHeaders.length > 0 && (
                <Form.Select
                  value={formData.fileLeadSourceColumn || ""}
                  onChange={(e) =>
                    handleInputChange("fileLeadSourceColumn", e.target.value)
                  }
                  className="rounded-3 shadow-sm custom-select"
                  aria-label="Select column for lead source"
                >
                  <option value="">Select Column</option>
                  {excelHeaders.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </Form.Select>
              )}
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center fw-semibold">
              <FaUser className="me-2" />
              Lead Assignment (Optional)
            </Form.Label>
            <Form.Select
              value={formData.leadAssignmentType || ""}
              onChange={(e) =>
                handleInputChange(
                  "leadAssignmentType",
                  e.target.value as "single" | "file-based" | null
                )
              }
              className="rounded-3 shadow-sm custom-select mb-3"
              aria-label="Select lead assignment type"
            >
              <option value="">None</option>
              <option value="single">Assign to Single Lead Owner</option>
              {excelHeaders.length > 0 && (
                <option value="file-based">Assign Based on File</option>
              )}
            </Form.Select>
            {formData.leadAssignmentType === "single" && (
              <Form.Select
                value={formData.singleLeadOwner?.id || ""}
                onChange={(e) => {
                  const user = users.find((u) => u.id === e.target.value);
                  handleInputChange("singleLeadOwner", user);
                }}
                className="rounded-3 shadow-sm custom-select"
                aria-label="Select lead owner"
              >
                <option value="">Select Lead Owner</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </Form.Select>
            )}
            {formData.leadAssignmentType === "file-based" &&
              excelHeaders.length > 0 && (
                <Form.Select
                  value={formData.fileLeadOwnerColumn || ""}
                  onChange={(e) =>
                    handleInputChange("fileLeadOwnerColumn", e.target.value)
                  }
                  className="rounded-3 shadow-sm custom-select"
                  aria-label="Select column for lead owner"
                >
                  <option value="">Select Column</option>
                  {excelHeaders.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </Form.Select>
              )}
            <Form.Text className="text-muted mt-2 d-block">
              <small>
                Mobile numbers validated using the phonenumbers Python package.{" "}
                <FaInfoCircle className="text-primary" />
              </small>
            </Form.Text>
          </Form.Group>
        
        </Form>
      </Card.Body>
    </Card>
  );

  const renderStep3 = () => (
    <Card className="shadow-sm border-0 rounded-4 overflow-hidden glass-card animate__fadeInUp">
      <Card.Header className="bg-gradient-primary text-white p-4">
        <div className="d-flex align-items-center">
          <FaMap className="me-3 fs-4" />
          <h5 className="mb-0 fw-bold">Step 3: Map Fields</h5>
        </div>
      </Card.Header>
      <Card.Body className="p-5 bg-glass">
        {validationErrors.length > 0 && (
          <Alert
            variant="danger"
            className="rounded-3 p-3 shadow-sm animate__shakeX"
          >
            {validationErrors.map((err, idx) => (
              <div key={idx} className="d-flex align-items-center">
                <FaInfoCircle className="me-2 text-danger" />
                {err}
              </div>
            ))}
          </Alert>
        )}
        <div
          style={{ maxHeight: "450px", overflowY: "auto" }}
          className="rounded-3 border p-4 bg-glass shadow-sm"
        >
          <Row>
            {(formData.fileLeadOwnerColumn ||
              formData.fileLeadSourceColumn) && (
              <div className="mb-4 bg-glass p-3 rounded-3 shadow-sm">
                <h6 className="fw-bold mb-2">Pre-Selected Assignments</h6>
                {formData.fileLeadOwnerColumn && (
                  <div>
                    <FaUserTie className="me-2 text-primary" />
                    Lead Owner Column:{" "}
                    <strong>{formData.fileLeadOwnerColumn}</strong>
                  </div>
                )}
                {formData.fileLeadSourceColumn && (
                  <div>
                    <FaBriefcase className="me-2 text-primary" />
                    Lead Source Column:{" "}
                    <strong>{formData.fileLeadSourceColumn}</strong>
                  </div>
                )}
              </div>
            )}
            <div className="mb-3 text-danger small">
              * Company Name, Company Phone, and Contact Person Name are
              mandatory.
            </div>

            <Col md={6}>
              <h6 className="d-flex align-items-center mb-4 fw-bold">
                <FaFileAlt className="me-2 text-primary" />
                Imported File Fields
              </h6>
              {excelHeaders.map((header) => (
                <div
                  key={header}
                  className="d-flex align-items-center mb-3 p-3 bg-glass rounded-3 shadow-sm border border-light transition-all hover-scale"
                >
                  <FaBuilding className="me-2 text-muted" />
                  <strong className="text-truncate flex-grow-1">
                    {header}
                  </strong>
                </div>
              ))}
            </Col>
            <Col md={6}>
              <h6 className="d-flex align-items-center mb-4 fw-bold">
                <FaMapMarkedAlt className="me-2 text-primary" />
                Map to Contact Fields
              </h6>

              {excelHeaders.map((header) => (
                <div key={header} className="mb-3">
                  <Form.Select
                    value={formData.fieldMappings[header] || ""}
                    onChange={(e) => {
                      handleMappingChange(header, e.target.value);
                      if (
                        e.target.value === "lead_owner" ||
                        e.target.value === "leadsource_id"
                      ) {
                        handleLeadOwnerMapping(header, e.target.value);
                      }
                    }}
                    className="rounded-3 shadow-sm custom-select transition-all"
                    aria-label={`Map field for ${header}`}
                  >
                    <option value="">Select Field</option>
                    {contactFields.map((field) => (
                      <option key={field} value={field}>
                        <span className="d-flex align-items-center">
                          {field.includes("company") && (
                            <FaBuilding className="me-1" />
                          )}
                          {field.includes("contact") && (
                            <FaUser className="me-1" />
                          )}
                          {field.includes("branch") && (
                            <FaHome className="me-1" />
                          )}
                          {field.includes("leadsource") && (
                            <FaBriefcase className="me-1" />
                          )}
                          {(field.includes("remark") ||
                            field.includes("notes")) && (
                            <FaComment className="me-1" />
                          )}
                          {field.includes("gst") && (
                            <FaFileInvoice className="me-1" />
                          )}
                          {field.includes("country") && (
                            <FaGlobeAsia className="me-1" />
                          )}
                          {field.includes("city") && (
                            <FaCity className="me-1" />
                          )}
                          {field.includes("address") && (
                            <FaMapPin className="me-1" />
                          )}
                          {field
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}
            </Col>
          </Row>
          <Badge bg="primary" className="mt-3 px-3 py-2 shadow-sm">
            Mapped:{" "}
            {Object.values(formData.fieldMappings).filter((v) => v).length} /{" "}
            {excelHeaders.length} fields
          </Badge>
        </div>

         {/* ------------------ Per-file mapping UI ------------------ */}
      {formData.leadAssignmentType === "file-based" &&
        formData.fileLeadOwnerColumn &&
        (() => {
          const col = formData.fileLeadOwnerColumn as string;
          return (
            <div className="mt-4">
              <h6 className="fw-bold mb-2">Map file values → Lead Owners</h6>
              {uniqueValuesForColumn(col).map((val) => (
                <div key={val} className="d-flex align-items-center mb-2">
                  <div className="flex-grow-1 text-truncate me-3">{val}</div>
                  <Form.Select
                    value={(valueMappings[col] || {})[val] || ""}
                    onChange={(e) => {
                      setValueMappings((prev) => {
                        const colMap = { ...(prev[col] || {}) };
                        colMap[val] = e.target.value;
                        return { ...prev, [col]: colMap };
                      });
                    }}
                    className="w-50"
                  >
                    <option value="">-- map to user --</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}
              <Form.Text className="text-muted">
                Map file values to existing users. Unmapped values are ignored.
              </Form.Text>
            </div>
          );
        })()}

      {/* lead source per-file mapping — detect which header mapped to leadsource_id */}
      {Object.keys(formData.fieldMappings).find(
        (k) => formData.fieldMappings[k] === "leadsource_id"
      ) &&
        (() => {
          const col = Object.keys(formData.fieldMappings).find(
            (k) => formData.fieldMappings[k] === "leadsource_id"
          )!;
          return (
            <div className="mt-4">
              <h6 className="fw-bold mb-2">
                Map file values → Lead Sources (column: {col})
              </h6>
              {uniqueValuesForColumn(col).map((val) => (
                <div key={val} className="d-flex align-items-center mb-2">
                  <div className="flex-grow-1 text-truncate me-3">{val}</div>
                  <Form.Select
                    value={(valueMappings[col] || {})[val] || ""}
                    onChange={(e) => {
                      setValueMappings((prev) => {
                        const colMap = { ...(prev[col] || {}) };
                        colMap[val] = e.target.value;
                        return { ...prev, [col]: colMap };
                      });
                    }}
                    className="w-50"
                  >
                    <option value="">-- map to lead source --</option>
                    {leadSources.map((ls) => (
                      <option key={ls.LeadSource_id} value={ls.LeadSource_id}>
                        {ls.LeadSource_name} ({ls.LeadSource_id})
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}
              <Form.Text className="text-muted">
                Map file lead source names to system LeadSource IDs.
              </Form.Text>
            </div>
          );
        })()}
      {/* ------------------ End mapping UI ------------------ */}
      {/* lead source per-file mapping */}
      {formData.leadSourceType === "file-based" &&
        formData.fileLeadSourceColumn &&
        (() => {
          const col = formData.fileLeadSourceColumn as string;
          return (
            <div className="mt-4">
              <h6 className="fw-bold mb-2">Map file values → Lead Sources</h6>
              {uniqueValuesForColumn(col).map((val) => (
                <div key={val} className="d-flex align-items-center mb-2">
                  <div className="flex-grow-1 text-truncate me-3">{val}</div>
                  <Form.Select
                    value={(valueMappings[col] || {})[val] || ""}
                    onChange={(e) => {
                      setValueMappings((prev) => {
                        const colMap = { ...(prev[col] || {}) };
                        colMap[val] = e.target.value;
                        return { ...prev, [col]: colMap };
                      });
                    }}
                    className="w-50"
                  >
                    <option value="">-- map to lead source --</option>
                    {leadSources.map((ls) => (
                      <option key={ls.LeadSource_id} value={ls.LeadSource_id}>
                        {ls.LeadSource_name} ({ls.LeadSource_id})
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}
              <Form.Text className="text-muted">
                Map Excel values to system lead sources. Unmapped values will be
                ignored.
              </Form.Text>
            </div>
          );
        })()}
      </Card.Body>
     
    </Card>
  );

  const renderStep4 = () => (
    <Card className="shadow-sm border-0 rounded-4 overflow-hidden glass-card animate__fadeInUp">
      <Card.Header className="bg-gradient-primary text-white p-4">
        <div className="d-flex align-items-center">
          <FaFileAlt className="me-3 fs-4" />
          <h5 className="mb-0 fw-bold">Step 4: Review & Confirm</h5>
        </div>
      </Card.Header>
      <Card.Body className="p-5 bg-glass">
        <Row>
          <Col md={3}>
            <h6 className="d-flex align-items-center mb-4 fw-bold">
              <FaUpload className="me-2 text-primary" />
              Step 1: File Details
            </h6>
            <ul className="list-unstyled">
              <li className="d-flex align-items-center mb-3 p-3 bg-glass rounded-3 shadow-sm border border-light transition-all hover-scale">
                <FaFileExcel className="me-2 text-success" />
                <small className="text-truncate">
                  {formData.file?.name || "No file selected"}
                </small>
              </li>
            </ul>
          </Col>
          <Col md={3}>
            <h6 className="d-flex align-items-center mb-4 fw-bold">
              <FaMapMarkedAlt className="me-2 text-primary" />
              Step 2: Actions
            </h6>
            <ul className="list-unstyled">
              <li className="d-flex align-items-center mb-3 p-3 bg-glass rounded-3 shadow-sm border border-light transition-all hover-scale">
                <FaMobileAlt className="me-2" />
                <small>
                  Duplicate Mobile:{" "}
                  {formData.allowDuplicatesMobile ? "Allowed" : "Not Allowed"}
                </small>
              </li>
              <li className="d-flex align-items-center mb-3 p-3 bg-glass rounded-3 shadow-sm border border-light transition-all hover-scale">
                <FaGlobe className="me-2" />
                <small>
                  Country Code Check:{" "}
                  {formData.mobileCountryCodeCheck ? "Enabled" : "Disabled"}
                </small>
              </li>
              <li className="d-flex align-items-center mb-3 p-3 bg-glass rounded-3 shadow-sm border border-light transition-all hover-scale">
                <FaUser className="me-2" />
                <small>
                  Lead Assignment: {formData.leadAssignmentType || "None"}
                </small>
              </li>
            </ul>
          </Col>
          <Col md={3}>
            <h6 className="d-flex align-items-center mb-4 fw-bold">
              <FaMap className="me-2 text-primary" />
              Step 3: Field Mapping
            </h6>
            <ul className="list-unstyled">
              <li className="d-flex align-items-center mb-3 p-3 bg-glass rounded-3 shadow-sm border border-light transition-all hover-scale">
                <FaCheckCircle className="me-2 text-success" />
                <small>
                  Mapped:{" "}
                  {
                    Object.values(formData.fieldMappings).filter((v) => v)
                      .length
                  }{" "}
                  / {excelHeaders.length}
                </small>
              </li>
            </ul>
          </Col>
          <Col md={3}>
            <h6 className="d-flex align-items-center mb-4 fw-bold">
              <FaFileAlt className="me-2 text-primary" />
              Step 4: Summary
            </h6>
            <pre className="bg-light p-3 rounded-3 border text-dark small">
              {formData.leadAssignmentType === "file-based" &&
              formData.fileLeadOwnerColumn
                ? JSON.stringify(
                    leadSources.reduce((acc, ls) => {
                      if (ls.LeadSource_id && ls.LeadSource_name) {
                        acc[ls.LeadSource_id] = ls.LeadSource_name;
                      }
                      return acc;
                    }, {} as Record<string, string>),
                    null,
                    2
                  )
                : formData.singleLeadOwner
                ? `{"${formData.singleLeadOwner.name.toLowerCase()}":"${
                    formData.singleLeadOwner.name
                  }"}`
                : "{}"}
              {formData.singleLeadOwner && (
                <p>
                  Assigned To: <strong>{formData.singleLeadOwner.name}</strong>
                </p>
              )}
            </pre>

            {Object.values(formData.fieldMappings).includes(
              "leadsource_id"
            ) && (
              <Card className="mt-4 border-0 bg-glass shadow-sm rounded-3">
                <Card.Body>
                  <h6 className="fw-bold mb-3 d-flex align-items-center">
                    <FaBriefcase className="me-2 text-primary" />
                    Lead Source Mapping Preview
                  </h6>
                  <pre className="bg-light p-3 rounded-3 border text-dark small">
                    {JSON.stringify(
                      leadSources.reduce((acc, ls) => {
                        if (ls.LeadSource_id && ls.LeadSource_name) {
                          acc[ls.LeadSource_name.toLowerCase()] =
                            ls.LeadSource_name;
                        }
                        return acc;
                      }, {} as Record<string, string>),
                      null,
                      2
                    )}
                  </pre>
                </Card.Body>
              </Card>
            )}
            {formData.leadSourceType === "file-based" &&
              formData.fileLeadSourceColumn && (
                <Card className="mt-4 border-0 bg-glass shadow-sm rounded-3">
                  <Card.Body>
                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                      <FaBriefcase className="me-2 text-primary" />
                      Lead Source Mapping Preview
                    </h6>
                    <pre className="bg-light p-3 rounded-3 border text-dark small">
                      {JSON.stringify(
                        Object.entries(
                          valueMappings[formData.fileLeadSourceColumn] || {}
                        ).reduce(
                          (acc, [k, v]) => ((acc[k.toLowerCase()] = v), acc),
                          {} as Record<string, string>
                        ),
                        null,
                        2
                      )}
                    </pre>
                  </Card.Body>
                </Card>
              )}

            {formData.leadSourceType === "single" &&
              formData.singleLeadSource && (
                <Card className="mt-4 border-0 bg-glass shadow-sm rounded-3">
                  <Card.Body>
                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                      <FaBriefcase className="me-2 text-primary" />
                      Selected Lead Source
                    </h6>
                    <p className="fw-semibold text-dark">
                      {formData.singleLeadSource.LeadSource_name} (
                      {formData.singleLeadSource.LeadSource_name})
                    </p>
                  </Card.Body>
                </Card>
              )}

            <div className="text-center p-4 bg-success bg-opacity-10 rounded-3 shadow-sm border border-light animate__pulse">
              <FaCheckCircle className="fs-2 text-success mb-3" />
              <p className="mb-0 fw-bold text-success">Ready to Upload</p>
            </div>
          </Col>
        </Row>
        {uploadErrors.length > 0 && (
          <Alert variant="warning" className="mt-4 rounded-3 p-3 shadow-sm">
            <h6 className="fw-bold text-danger mb-3">
              Import completed with {uploadErrors.length} errors:
            </h6>
            <ul
              className="mb-0"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {uploadErrors.map((err, idx) => (
                <li key={idx}>
                  Row {err.row}: {err.error}
                  {err.phone ? ` (${err.phone})` : ""}{" "}
                  {err.email ? ` (${err.email})` : ""}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );

  const handleConfirmUpload = async () => {
    if (loading) return;
    setLoading(true);

    try {
      if (!formData.file) {
        setValidationErrors(["Please select a file to upload."]);
        setLoading(false);
        return;
      }

      // === Build import options ===
      const opts: ImportContactsOptions = {
        file: formData.file,
        Action_type:
          formData.actionType === "add"
            ? "Add_New_Record"
            : "Update_Existing_Record",
        countrycode: formData.mobileCountryCodeCheck ? "Yes" : "No",
        field_mapping: formData.fieldMappings,
      };

      // === Country code handling ===
      if (!formData.mobileCountryCodeCheck && formData.selectedCountryCode) {
        opts.countrycodevalue = formData.selectedCountryCode.code;
      }

      // === Duplicate / Update handling ===
      if (formData.actionType === "add" || formData.actionType === "update") {
        if (formData.allowDuplicatesMobile || formData.allowDuplicatesEmail) {
          opts.Allow_duplicate = "Yes";
          if (formData.allowDuplicatesMobile) opts.Check_Base = "Mobile";
          else if (formData.allowDuplicatesEmail) opts.Check_Base = "Email";
        } else {
          opts.Allow_duplicate = "No";
        }
      } else if (formData.actionType === "update") {
        opts.Check_Base =
          formData.updateBasedOn === "mobile" ? "Mobile" : "Email";
      }

      // === Lead Assignment Handling ===
      if (
        formData.leadAssignmentType === "single" &&
        formData.singleLeadOwner
      ) {
        opts.leadAssign = "AssignSingleUser";
        opts.lead_id_single = formData.singleLeadOwner.id;
      } else if (
        formData.leadAssignmentType === "file-based" &&
        formData.fileLeadOwnerColumn
      ) {
        opts.leadAssign = "AssignPerUser";
        opts.lead_id_column_name = formData.fileLeadOwnerColumn;

        // Build mapping JSON (name → userId)
        const mapForCol = valueMappings[formData.fileLeadOwnerColumn] || {};
        const normalized: Record<string, string> = {};
        Object.entries(mapForCol).forEach(([fileVal, id]) => {
          if (id && fileVal && fileVal.trim() !== "") {
            normalized[fileVal.trim().toLowerCase()] = id;
          }
        });

        // Validate completeness
        const uniques = uniqueValuesForColumn(formData.fileLeadOwnerColumn);
        const unmapped = uniques.filter(
          (v) => !normalized[v.trim().toLowerCase()]
        );
        if (unmapped.length) {
          setValidationErrors([
            `Please map ${unmapped.length} owner values (e.g. "${unmapped
              .slice(0, 3)
              .join(", ")}") or remove the mapping.`,
          ]);
          setLoading(false);
          return;
        }

        if (Object.keys(normalized).length) opts.lead_id_mapping = normalized;
      }

      // === Lead Source Handling ===
      if (formData.leadSourceType === "single" && formData.singleLeadSource) {
        opts.leadsource_type = "SingleLeadSource";
        opts.leadsource_id = formData.singleLeadSource.LeadSource_id;
      } else if (
        formData.leadSourceType === "file-based" &&
        formData.fileLeadSourceColumn
      ) {
        opts.leadsource_type = "PerLeadSource";
        opts.lead_source_column_name = formData.fileLeadSourceColumn;

        const mapForCol = valueMappings[formData.fileLeadSourceColumn] || {};
        const normalized: Record<string, string> = {};
        Object.entries(mapForCol).forEach(([fileVal, id]) => {
          if (id && fileVal && fileVal.trim() !== "") {
            normalized[fileVal.trim().toLowerCase()] = id;
          }
        });

        // Validate completeness
        const uniques = uniqueValuesForColumn(formData.fileLeadSourceColumn);
        const unmapped = uniques.filter(
          (v) => !normalized[v.trim().toLowerCase()]
        );
        if (unmapped.length) {
          setValidationErrors([
            `Please map ${unmapped.length} lead source values (e.g. "${unmapped
              .slice(0, 3)
              .join(", ")}") or remove the mapping.`,
          ]);
          setLoading(false);
          return;
        }

        if (Object.keys(normalized).length)
          opts.leadsource_mapping = normalized;
      }

      // === Call API ===
      const result: ImportContactsResult | null = await importContacts(opts);

      if (result) {
  if (result.errors?.length) {
    console.warn("⚠️ Import completed with errors:", result.errors);
    setUploadErrors(result.errors);
  } 

  else {
    setUploadErrors([]);
    toast.success("Contacts imported successfully. Redirecting to contacts...");

    // Redirect to contact list after 3 seconds
    setTimeout(() => {
      navigate(route.contactList);
    }, 3000);
  }
}
    } catch (error) {
      console.error("❌ Upload failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
     <PageLoadProgressBar />
      <div className="page-wrapper">
        <Container fluid className="p-4 bg-light full-width-container">
          <Row className="justify-content-center">
            <Col xs={12} className="import-page-col">
              <div className="progress-container mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  {[
                    { label: "Upload File", tooltip: "Upload your Excel file" },
                    {
                      label: "Configure Actions",
                      tooltip: "Set import preferences",
                    },
                    {
                      label: "Map Fields",
                      tooltip: "Map file fields to system fields",
                    },
                    {
                      label: "Review",
                      tooltip: "Review and confirm your settings",
                    },
                  ].map((stepItem, idx) => (
                    <OverlayTrigger
                      key={idx}
                      placement="top"
                      overlay={<Tooltip>{stepItem.tooltip}</Tooltip>}
                    >
                      <div className="text-center position-relative">
                        <div
                          className={`step-circle ${
                            step > idx + 1
                              ? "completed"
                              : step === idx + 1
                              ? "active"
                              : ""
                          } transition-all`}
                        >
                          {step > idx + 1 ? <FaCheckCircle /> : idx + 1}
                        </div>
                        <small className="text-muted fw-semibold d-block mt-2">
                          {stepItem.label}
                        </small>
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
                <div
                  className="progress rounded-pill shadow-sm mt-3"
                  style={{ height: "6px" }}
                >
                  <div
                    className="progress-bar bg-gradient-primary rounded-pill transition-all"
                    style={{ width: `${(step / 4) * 100}%` }}
                    role="progressbar"
                    aria-valuenow={step}
                    aria-valuemin={1}
                    aria-valuemax={4}
                    aria-label={`Progress: Step ${step} of 4`}
                  />
                </div>
              </div>
              <div className="step-card-wrapper">
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && renderStep3()}
                {step === 4 && renderStep4()}
              </div>
              <div className="d-flex justify-content-between mt-5 pt-4 border-top">
                {step > 1 && (
                  <Button
                    variant="outline-secondary"
                    onClick={prevStep}
                    className="rounded-pill px-4 py-2 transition-all hover-scale"
                    aria-label="Go to previous step"
                  >
                    <FaArrowLeft className="me-2" /> Previous
                  </Button>
                )}
                <div></div>
                {step < 4 ? (
                  <Button
                    variant="primary"
                    onClick={nextStep}
                    disabled={validationErrors.length > 0}
                    className="rounded-pill px-4 py-2 transition-all hover-scale"
                    aria-label="Go to next step"
                  >
                    Next <FaArrowRight className="ms-2" />
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    onClick={handleConfirmUpload}
                    disabled={loading}
                    className="rounded-pill px-4 py-2 transition-all hover-scale"
                    aria-label="Confirm and upload"
                  >
                    {loading ? (
                      <FaSpinner className="me-2 spinner" />
                    ) : (
                      <FaCheckCircle className="me-2" />
                    )}
                    {loading ? "Uploading..." : "Confirm & Upload"}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700:800&display=swap');

  :root {
    --primary: #4F46E5; /* Indigo-600 */
    --primary-dark: #4338CA; /* Indigo-700 */
    --success: #10B981; /* Emerald-500 */
    --success-dark: #059669; /* Emerald-600 */
    --warning: #F59E0B; /* Amber-500 */
    --danger: #EF4444; /* Red-500 */
    --background: #F1F5F9; /* Slate-100 */
    --card-bg: rgba(255, 255, 255, 0.85);
    --text-primary: #1E293B; /* Slate-800 */
    --text-secondary: #64748B; /* Slate-500 */
    --border-light: rgba(209, 213, 219, 0.4);
    --shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .page-wrapper {
    min-height: 100vh;
    background: linear-gradient(180deg, var(--background) 0%, #E5E7EB 100%);
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    overflow-x: hidden;
    padding-bottom: 2rem;
  }
    

  .bg-gradient-primary {
    background: linear-gradient(135deg, var(--primary) 0%, #7C3AED 100%);
    color: white;
  }

  .glass-card {
    background: var(--card-bg);
    backdrop-filter: blur(12px);
    border: 1px solid var(--border-light);
    border-radius: 16px;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .glass-card:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow);
  }

  .bg-glass {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid var(--border-light);
    border-radius: 12px;
  }

  .progress-container {
    position: relative;
    width: 100%;
    padding: 1rem 0;
  }

  .step-circle {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #E2E8F0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-secondary);
    transition: all 0.3s ease;
    box-shadow: var(--shadow);
  }

  .step-circle.active {
    background: var(--primary);
    color: white;
    box-shadow: 0 0 0 6px rgba(79, 70, 229, 0.2);
    transform: scale(1.1);
  }

  .step-circle.completed {
    background: var(--success);
    color: white;
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.2);
  }

  .progress {
    height: 8px;
    background: #E5E7EB;
    border-radius: 999px;
    overflow: hidden;
  }

  .progress-bar {
    background: linear-gradient(90deg, var(--primary) 0%, #7C3AED 100%);
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .transition-all {
    transition: all 0.3s ease;
  }

  .hover-scale:hover {
    transform: scale(1.03);
    box-shadow: var(--shadow);
  }

  .hover-bg:hover {
    background: rgba(255, 255, 255, 0.9) !important;
  }

  .custom-input, .custom-select {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid var(--border-light) !important;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    color: var(--text-primary);
    transition: all 0.2s ease;
  }

  .custom-input:focus, .custom-select:focus {
    border-color: var(--primary) !important;
    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.15) !important;
    background: white !important;
  }

  .btn-primary {
    background: var(--primary) !important;
    border-color: var(--primary) !important;
    font-weight: 500;
    padding: 0.75rem 1.5rem;
    border-radius: 999px;
    transition: all 0.2s ease;
  }

  .btn-primary:hover {
    background: var(--primary-dark) !important;
    border-color: var(--primary-dark) !important;
    transform: translateY(-2px);
  }

  .btn-success {
    background: var(--success) !important;
    border-color: var(--success) !important;
    font-weight: 500;
    padding: 0.75rem 1.5rem;
    border-radius: 999px;
    transition: all 0.2s ease;
  }

  .btn-success:hover {
    background: var(--success-dark) !important;
    border-color: var(--success-dark) !important;
    transform: translateY(-2px);
  }

  .btn-outline-secondary {
    border-color: var(--border-light) !important;
    color: var(--text-secondary) !important;
    padding: 0.75rem 1.5rem;
    border-radius: 999px;
    transition: all 0.2s ease;
  }

  .btn-outline-secondary:hover {
    background: #F1F5F9 !important;
    color: var(--text-primary) !important;
    transform: translateY(-2px);
  }

  .alert {
    border-radius: 12px;
    padding: 1rem;
    font-size: 0.9rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(8px);
    border: 1px solid var(--border-light);
  }

  .alert-danger {
    background: rgba(239, 68, 68, 0.1);
    color: var(--danger);
    border-color: rgba(239, 68, 68, 0.3);
  }

  .alert-warning {
    background: rgba(245, 158, 11, 0.1);
    color: var(--warning);
    border-color: rgba(245, 158, 11, 0.3);
  }

  .badge {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
    border-radius: 999px;
    font-weight: 500;
  }

  .spinner {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .animate__fadeInUp {
    animation: fadeInUp 0.6s ease-out;
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .animate__shakeX {
    animation: shakeX 0.5s ease;
  }

  @keyframes shakeX {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-8px); }
    40%, 80% { transform: translateX(8px); }
  }

  .animate__pulse {
    animation: pulse 1.5s infinite ease-in-out;
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }

  /* Country Dropdown */
  .position-absolute {
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px solid var(--border-light);
    box-shadow: var(--shadow);
    max-height: 320px;
    overflow-y: auto;
  }

  /* Scrollbar Styling */
  .position-absolute::-webkit-scrollbar,
  .bg-glass::-webkit-scrollbar {
    width: 8px;
  }

  .position-absolute::-webkit-scrollbar-track,
  .bg-glass::-webkit-scrollbar-track {
    background: rgba(229, 231, 235, 0.3);
    border-radius: 999px;
  }

  .position-absolute::-webkit-scrollbar-thumb,
  .bg-glass::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 999px;
  }

  /* Icon Consistency */
  svg {
    font-size: 1.2rem;
    transition: transform 0.2s ease;
  }

  .hover-scale:hover svg {
    transform: scale(1.1);
  }

  /* Responsive Adjustments */
  @media (max-width: 768px) {
    .progress-container {
      padding: 0.5rem 0;
    }
    .step-circle {
      width: 36px;
      height: 36px;
      font-size: 16px;
    }
    .card-body {
      padding: 1.25rem !important;
    }
    .col-md-3, .col-md-6 {
      margin-bottom: 1.25rem;
    }
    .btn, .custom-input, .custom-select {
      font-size: 0.9rem;
      padding: 0.6rem 1rem;
    }
  }

  @media (max-width: 576px) {
    .step-circle {
      width: 32px;
      height: 32px;
      font-size: 14px;
    }
    .progress {
      height: 6px !important;
    }
    .card-header {
      padding: 1rem !important;
    }
    .card-body {
      padding: 1rem !important;
    }
    .btn {
      padding: 0.5rem 1rem;
    }
      .cursor-pointer {
  cursor: pointer;
}

.hover-border-primary:hover {
  border-color: var(--primary) !important;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
  transform: translateY(-2px);
}

  }
`}</style>
    </>
  );
};

export default ImportPage;
